import {HoistProps, LayoutProps, SelectOption, creates, hoistCmp} from '@xh/hoist/core';
import {popover} from '@xh/hoist/kit/blueprint';
import {panel} from '@xh/hoist/desktop/cmp/panel';
import {textInput} from '@xh/hoist/desktop/cmp/input';
import {filler, hbox, p, placeholder, span, vbox, vspacer} from '@xh/hoist/cmp/layout';
import './SecurityPicker.scss';
import {splitLayoutProps} from '@xh/hoist/utils/react';
import {SecurityPickerModel} from './SecurityPickerModel';
import {Security, SecuritySubTypeEnum} from '../../data/DataTypes';
import parse from 'html-react-parser';
import {Icon} from '@xh/hoist/icon';
import {grid} from '@xh/hoist/cmp/grid';
import {secCurrencyBadge, secExchangeBadge, secSubTypeBadge} from '../secBadges/SecBadges';
import {ReactNode} from 'react';
import classNames from 'classnames';

interface SecurityPickerProps extends HoistProps<SecurityPickerModel>, LayoutProps {
    ticker?: string;
    disabled?: boolean;
    keepSelection?: boolean;
    placeholder?: string;
    showSearchIcon?: boolean;
    autofocus?: boolean;
    /** True to render flush/squared-off for use as a direct item within a compact toolbar. */
    compact?: boolean;
    onSecuritySelected?: (security: Security) => void;
    onOpen?: (state: boolean) => void;
}

export const securityPicker = hoistCmp.factory<SecurityPickerProps>({
    model: creates(SecurityPickerModel),
    render({model, ...props}) {
        const [layoutProps] = splitLayoutProps(props);
        const panelSize = {width: 375, height: 500};

        return popover({
            className: 'ticker-picker',
            interactionKind: 'click',
            onInteraction: (nextState, e) => model.onInteraction(nextState, e),
            position: 'bottom-left',
            minimal: true,
            autoFocus: false,
            targetProps: props.compact ? {className: 'square-button-target'} : null,
            item: textInput({
                bind: 'query',
                inputRef: model.inputRef,
                commitOnChange: true,
                disabled: props.disabled,
                className: classNames('ticker-picker__search', props.compact && 'ticker-picker__search--compact'),
                rightElement: props.showSearchIcon ? Icon.search() : null,
                autoFocus: props.autofocus,
                placeholder: props.placeholder,
                onKeyDown: e => model.onSearchKeyDown(e),
                ...layoutProps
            }),

            isOpen: model.isOpen,
            content: panel({
                item: model.query
                    ? grid({
                          model: model.securityGrid,
                          ...panelSize
                      })
                    : placeholder({
                          items: [
                              Icon.search(),
                              p('search expressions:'),
                              p('equity'),
                              p('.etf'),
                              p('^index'),
                              p('/future'),
                              p('@option'),
                              p('=forex'),
                              p('#crypto'),
                              p('%real estate')
                          ],
                          style: {...panelSize}
                      }),
                mask: model.apiTask
            })
        });
    }
});

export const itemRenderer = hoistCmp.factory<SecurityPickerModel>({
    render({model, record}) {
        const security = record.raw;

        return vbox({
            className: 'ticker-picker__item',
            items: [
                hbox({
                    gap: 5,
                    items: [
                        span({
                            className: 'ticker-picker__item-ticker',
                            item: parse(security.displayTicker)
                        }),
                        filler(),
                        security.exchange ? secExchangeBadge(security.exchange) : null,
                        secSubTypeBadge(security.subType)
                    ]
                }),
                span({
                    className: 'ticker-picker__item-name',
                    item: security.displayName ? parse(security.displayName) : 'N/A'
                })
            ]
        });
    }
});

export interface SecuritySelectOption extends SelectOption {
    displayTicker: string;
    displayName: string;
    exchange: string;
    currency: string;
    subType: SecuritySubTypeEnum;
}

export function securityOptionRenderer(option: SecuritySelectOption): ReactNode {
    return vbox({
        className: 'ticker-picker__item',
        style: {height: 55, borderBottom: 'var(--xh-border-solid)'},
        items: [
            hbox({
                alignItems: 'center',
                gap: 5,
                items: [
                    span({
                        className: 'ticker-picker__item-ticker',
                        item: option.displayTicker ? parse(option.displayTicker) : option.value
                    }),
                    filler(),
                    secSubTypeBadge(option.subType),
                    option.exchange ? secExchangeBadge(option.exchange) : null,
                    option.currency ? secCurrencyBadge(option.currency) : null
                ]
            }),
            vspacer(8),
            span({
                className: 'ticker-picker__item-name',
                item: option.displayName ? parse(option.displayName) : option.label
            })
        ]
    });
}
